import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Users,
    Plus,
    Search,
    Trash2,
    Loader2,
    Mail,
    Phone,
    Smartphone,
    Building2,
    MapPin,
    FolderKanban,
    Share2,
    ChevronLeft,
    ClipboardList,
    Copy,
} from "lucide-react";
import TopNav from "../components/TopNav";
import NewContactDialog from "../components/NewContactDialog";
import SaveToWorkgroupDialog from "../components/SaveToWorkgroupDialog";
import ContactDuplicatesDialog from "../components/ContactDuplicatesDialog";
import { ContactLaunchIcons } from "./LeadsPage";
import {
    listContacts,
    createContact,
    updateContact,
    deleteContact,
    listContactProjects,
    createLeadFromContact,
} from "../lib/api";

const INPUT_CLS =
    "w-full bg-[#0A1128] border border-[#2A3655] rounded-sm px-2.5 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00A3FF] transition-colors";

const FIELDS = [
    { key: "name", label: "Name", placeholder: "Full name", icon: null, required: true },
    { key: "company", label: "Company", placeholder: "Company name", icon: Building2 },
    { key: "address", label: "Address", placeholder: "Street address", icon: MapPin },
    { key: "city", label: "City", placeholder: "City" },
    { key: "country", label: "Country", placeholder: "Country" },
    { key: "email", label: "Email", placeholder: "name@company.com", icon: Mail, type: "email" },
    { key: "work_phone", label: "Work phone", placeholder: "(555) 123-4567", icon: Phone, type: "tel" },
    { key: "cell_phone", label: "Cell phone", placeholder: "(555) 987-6543", icon: Smartphone, type: "tel" },
];

export default function ContactsPage() {
    const navigate = useNavigate();
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [newOpen, setNewOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    // iter-185 — Duplicates finder dialog.
    const [dupOpen, setDupOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const data = await listContacts();
            setContacts(data);
            // Auto-select only on desktop/tablet-landscape (≥ md). On mobile
            // we want users to LAND on the list — the master/detail pattern
            // hides the list when something is selected, so auto-selecting
            // would park them on a detail pane they didn't ask for.
            const isDesktop =
                typeof window !== "undefined" &&
                window.matchMedia("(min-width: 768px)").matches;
            if (isDesktop && !selectedId && data.length) setSelectedId(data[0].id);
        } catch {
            toast.error("Couldn't load contacts");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const handleNew = () => {
        // Open the dialog and let the user fill in real info before the
        // row is ever created (no more placeholder "New contact" stubs
        // polluting the list).
        setNewOpen(true);
    };

    const handleCreateFromDialog = async (payload) => {
        setCreating(true);
        try {
            const c = await createContact(payload);
            setContacts((prev) => [c, ...prev]);
            setSelectedId(c.id);
            toast.success("Contact added");
        } catch {
            toast.error("Couldn't create contact");
        } finally {
            setCreating(false);
        }
    };

    const handleUpdate = async (id, patch) => {
        setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
        try {
            await updateContact(id, patch);
        } catch {
            toast.error("Couldn't save");
            load();
        }
    };

    const handleDelete = async (id) => {
        const c = contacts.find((x) => x.id === id);
        if (!window.confirm(`Delete "${c?.name || "this contact"}"?`)) return;
        try {
            await deleteContact(id);
            const remaining = contacts.filter((x) => x.id !== id);
            setContacts(remaining);
            if (selectedId === id) setSelectedId(remaining[0]?.id || null);
            toast.success("Contact deleted");
        } catch {
            toast.error("Couldn't delete");
        }
    };

    // iter-184 — Mirror action: build a lead pre-filled with this
    // contact's fields. Idempotent server-side so a second click just
    // navigates the user to the already-linked lead row.
    const handleCreateLead = async (contact) => {
        try {
            const { lead, created } = await createLeadFromContact(contact.id);
            toast.success(
                created
                    ? `Lead created from ${contact.name || "contact"}`
                    : "This contact already has a lead — jumping to it.",
            );
            navigate(`/leads?highlight=${encodeURIComponent(lead.id)}`);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't create lead",
            );
        }
    };

    const filtered = contacts.filter((c) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return [c.name, c.company, c.email, c.city, c.country, c.work_phone, c.cell_phone]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q));
    });
    const active = contacts.find((c) => c.id === selectedId) || null;

    return (
        <div className="h-screen bg-[#05091A] text-white flex flex-col overflow-hidden blueprint-bg-soft">
            <TopNav active="contacts" />
            <main className="flex-1 flex min-h-0" data-testid="contacts-page">
                {/* Left column — list.
                    Mobile: show list OR detail (not both) so narrow portrait
                    screens aren't cut off. Desktop/tablet-landscape: both. */}
                <aside className={`${active ? "hidden md:flex" : "flex"} w-full md:w-80 shrink-0 border-r border-[#2A3655] bg-[#0A1128]/70 flex-col`}>
                    <div className="p-3 border-b border-[#2A3655] flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search contacts…"
                                data-testid="contacts-search"
                                className={`${INPUT_CLS} pl-8`}
                            />
                        </div>
                        <button
                            onClick={() => setDupOpen(true)}
                            data-testid="btn-find-duplicates"
                            title="Find and merge duplicates"
                            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-sm bg-amber-500/15 border border-amber-500/40 text-amber-400 hover:bg-amber-500/25 transition-colors"
                        >
                            <Copy className="w-4 h-4" />
                        </button>
                        <button
                            onClick={handleNew}
                            disabled={creating}
                            data-testid="btn-new-contact"
                            title="New contact"
                            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-sm bg-[#00A3FF]/15 border border-[#00A3FF]/40 text-[#00A3FF] hover:bg-[#00A3FF]/25 transition-colors disabled:opacity-50"
                        >
                            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="p-6 text-center text-slate-500 text-sm">
                                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                                Loading…
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="p-6 text-center text-slate-500 text-sm">
                                {contacts.length === 0 ? "No contacts yet — click + to add one." : "No matches."}
                            </div>
                        ) : (
                            <ul className="divide-y divide-[#2A3655]/50">
                                {filtered.map((c) => {
                                    const phone = (c.cell_phone || c.work_phone || "").trim();
                                    const email = (c.email || "").trim();
                                    return (
                                        <li
                                            key={c.id}
                                            className={`flex items-stretch ${
                                                selectedId === c.id
                                                    ? "bg-[#00A3FF]/10 border-l-2 border-[#00A3FF]"
                                                    : "hover:bg-[#1C253C]"
                                            }`}
                                        >
                                            <button
                                                onClick={() => setSelectedId(c.id)}
                                                data-testid={`contact-item-${c.id}`}
                                                className="flex-1 min-w-0 text-left px-4 py-3 transition-colors"
                                            >
                                                <div className="text-sm font-semibold text-white truncate">
                                                    {c.name || <span className="italic text-slate-500">Unnamed</span>}
                                                </div>
                                                {c.company && (
                                                    <div className="text-[11px] text-slate-400 truncate">
                                                        {c.company}
                                                    </div>
                                                )}
                                                {c.city && (
                                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                                                        {c.city}
                                                    </div>
                                                )}
                                            </button>
                                            {/* iter-99: quick-action icons next to each
                                                contact name — call / text / email
                                                without opening the detail pane. */}
                                            {(phone || email) && (
                                                <div className="shrink-0 flex items-center gap-1 pr-2">
                                                    {phone && (
                                                        <ContactLaunchIcons
                                                            value={phone}
                                                            kind="phone"
                                                            testIdRoot={`contact-list-${c.id}`}
                                                            compact
                                                        />
                                                    )}
                                                    {email && (
                                                        <ContactLaunchIcons
                                                            value={email}
                                                            kind="email"
                                                            testIdRoot={`contact-list-${c.id}`}
                                                            compact
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </aside>

                {/* Right column — details */}
                <section className={`${active ? "flex" : "hidden md:flex"} flex-1 min-h-0 overflow-y-auto p-4 md:p-8 flex-col`}>
                    {!active ? (
                        <div className="h-full flex items-center justify-center text-slate-500">
                            <div className="text-center">
                                <Users className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                                <div className="font-semibold">Select a contact</div>
                                <div className="text-xs text-slate-500 mt-1">Or create a new one from the left.</div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Mobile-only back button. */}
                            <button
                                type="button"
                                onClick={() => setSelectedId(null)}
                                data-testid="contact-back-to-list"
                                className="md:hidden inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-[#00A3FF] transition-colors -mt-1 mb-3 self-start"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" />
                                Back to contacts
                            </button>
                            <ContactEditor
                                key={active.id}
                                contact={active}
                                onUpdate={(patch) => handleUpdate(active.id, patch)}
                                onDelete={() => handleDelete(active.id)}
                                onShare={() => setShareOpen(true)}
                                onDone={() => setSelectedId(null)}
                                onCreateLead={() => handleCreateLead(active)}
                            />
                        </>
                    )}
                </section>
            </main>
            <NewContactDialog
                open={newOpen}
                onOpenChange={setNewOpen}
                onCreate={handleCreateFromDialog}
            />
            <SaveToWorkgroupDialog
                kind="contact"
                itemId={active?.id}
                itemName={active?.name}
                settingKey="share_contacts"
                open={shareOpen}
                onOpenChange={setShareOpen}
                onSaved={() => load()}
            />
            <ContactDuplicatesDialog
                open={dupOpen}
                onOpenChange={setDupOpen}
                onChanged={() => load()}
            />
        </div>
    );
}

function LinkedProjects({ contactId }) {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const data = await listContactProjects(contactId);
                if (!cancelled) setProjects(data);
            } catch {
                if (!cancelled) setProjects([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [contactId]);

    if (loading) {
        return (
            <div
                data-testid="contact-linked-projects-loading"
                className="text-xs text-slate-500 py-2"
            >
                <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-2" />
                Checking linked projects…
            </div>
        );
    }

    return (
        <div className="pt-4 mt-2 border-t border-[#2A3655]" data-testid="contact-linked-projects">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">
                <FolderKanban className="w-3.5 h-3.5 text-[#00A3FF]" />
                Linked projects · {projects.length}
            </div>
            {projects.length === 0 ? (
                <div className="text-xs text-slate-500 py-1">
                    No projects linked to this contact yet.
                </div>
            ) : (
                <ul className="space-y-1">
                    {projects.map((p) => (
                        <li key={p.id}>
                            <button
                                onClick={() => navigate(`/workspace/${p.id}`)}
                                data-testid={`contact-project-link-${p.id}`}
                                className="w-full text-left px-3 py-2 rounded-sm bg-[#0A1128] hover:bg-[#1C253C] border border-[#2A3655] hover:border-[#00A3FF]/60 transition-colors"
                            >
                                <div className="text-sm font-semibold text-white truncate">
                                    {p.name}
                                </div>
                                <div className="text-[11px] text-slate-400 truncate">
                                    {p.location} · {p.job_id}
                                </div>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function ContactEditor({ contact, onUpdate, onDelete, onShare, onDone, onCreateLead }) {
    const [local, setLocal] = useState(contact);
    const [saveState, setSaveState] = useState("idle"); // "idle" | "saving" | "saved"
    const savedTimerRef = React.useRef(null);
    useEffect(() => { setLocal(contact); }, [contact]);

    const save = async (key) => {
        const v = (local[key] ?? "").toString();
        if (v === (contact[key] ?? "").toString()) return;
        setSaveState("saving");
        try {
            await onUpdate({ [key]: v });
            setSaveState("saved");
            if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
            savedTimerRef.current = setTimeout(() => setSaveState("idle"), 2000);
        } catch {
            setSaveState("idle");
        }
    };

    return (
        <div className="max-w-3xl mx-auto space-y-4 pb-24 w-full">
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-[#2A3655]">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1 flex items-center gap-2">
                        Contact · {contact.id.slice(0, 6)}
                        {contact.workgroup_id && (
                            <span
                                data-testid="contact-shared-badge"
                                className="inline-flex items-center gap-1 text-[9px] bg-[#00A3FF]/15 text-[#00A3FF] px-1.5 py-0.5 rounded-sm border border-[#00A3FF]/40 normal-case tracking-normal"
                            >
                                <Users className="w-2.5 h-2.5" />
                                Shared
                            </span>
                        )}
                    </div>
                    <div className="text-2xl font-bold text-white">
                        {contact.name || "Unnamed"}
                    </div>
                    {/* Autosave status — tells the user they don't need a save
                        button. Shows 'Saving…' while the PATCH is in-flight,
                        'Saved ✓' for 2s after, then fades to a quiet
                        'Auto-saves as you type' hint. */}
                    <div
                        data-testid="contact-autosave-status"
                        className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold"
                    >
                        {saveState === "saving" ? (
                            <>
                                <Loader2 className="w-3 h-3 animate-spin text-[#00A3FF]" />
                                <span className="text-[#00A3FF]">Saving…</span>
                            </>
                        ) : saveState === "saved" ? (
                            <>
                                <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-emerald-500/20 text-emerald-300">
                                    ✓
                                </span>
                                <span className="text-emerald-300">Saved</span>
                            </>
                        ) : (
                            <span className="text-slate-500 normal-case tracking-normal font-normal">
                                Auto-saves as you type
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {!contact.workgroup_id && (
                        <button
                            onClick={onShare}
                            data-testid="btn-share-contact"
                            aria-label="Save to workgroup"
                            title="Save to workgroup"
                            className="shrink-0 p-2 rounded-sm text-slate-400 hover:text-[#00A3FF] hover:bg-[#00A3FF]/10 transition-colors"
                        >
                            <Share2 className="w-4 h-4" />
                        </button>
                    )}
                    {/* iter-184 — Create a lead pre-filled with this
                        contact's info. Idempotent server-side. */}
                    <button
                        onClick={onCreateLead}
                        data-testid="btn-create-lead-from-contact"
                        aria-label="Create lead from contact"
                        title="Create lead from this contact"
                        className="shrink-0 p-2 rounded-sm text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                    >
                        <ClipboardList className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onDelete}
                        data-testid="btn-delete-contact"
                        aria-label="Delete contact"
                        className="shrink-0 p-2 rounded-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {FIELDS.map((f) => {
                    const Icon = f.icon;
                    const val = (local[f.key] || "").toString().trim();
                    // tel: / mailto: quick-action affordance — tapping the
                    // leading icon with a populated value launches the
                    // phone dialer or default mail client. Icon stays a
                    // plain decoration when the field is empty or not a
                    // contact channel.
                    let href = null;
                    let title = null;
                    if (val) {
                        if (f.type === "email") {
                            href = `mailto:${val}`;
                            title = `Email ${val}`;
                        } else if (f.type === "tel") {
                            const digits = val.replace(/[^0-9+]/g, "");
                            if (digits) {
                                href = `tel:${digits}`;
                                title = `Call ${val}`;
                            }
                        }
                    }
                    return (
                        <div key={f.key} className={f.key === "name" || f.key === "address" ? "md:col-span-2" : ""}>
                            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                                {f.label}{f.required && <span className="text-[#00A3FF] ml-1">*</span>}
                            </label>
                            <div className="relative">
                                {Icon && (
                                    href ? (
                                        <a
                                            href={href}
                                            title={title}
                                            data-testid={`contact-action-${f.key}`}
                                            onClick={(e) => e.stopPropagation()}
                                            className="absolute left-1.5 top-1/2 -translate-y-1/2 p-1 rounded-sm text-[#00A3FF] hover:bg-[#00A3FF]/15 transition-colors"
                                        >
                                            <Icon className="w-3.5 h-3.5" />
                                        </a>
                                    ) : (
                                        <Icon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                                    )
                                )}
                                <input
                                    type={f.type || "text"}
                                    value={local[f.key] || ""}
                                    onChange={(e) => setLocal((l) => ({ ...l, [f.key]: e.target.value }))}
                                    onBlur={() => save(f.key)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") e.target.blur();
                                    }}
                                    placeholder={f.placeholder}
                                    data-testid={`contact-field-${f.key}`}
                                    className={`${INPUT_CLS} ${Icon ? "pl-8" : ""}`}
                                />
                            </div>
                        </div>
                    );
                })}
                <div className="md:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block mb-1">
                        Notes
                    </label>
                    <textarea
                        rows={3}
                        value={local.notes || ""}
                        onChange={(e) => setLocal((l) => ({ ...l, notes: e.target.value }))}
                        onBlur={() => save("notes")}
                        placeholder="Project history, preferences, follow-up dates…"
                        data-testid="contact-field-notes"
                        className={`${INPUT_CLS} resize-none`}
                    />
                </div>
            </div>
            <LinkedProjects contactId={contact.id} />
            {/* End-of-form footer — gives users visual closure + a one-tap way
                back to the list on mobile. There's no save button because
                fields auto-save on blur (see the status pill in the header). */}
            <div className="pt-4 border-t border-[#2A3655]/60 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-slate-500 inline-flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px]">
                        ✓
                    </span>
                    All changes save automatically.
                </div>
                {onDone && (
                    <button
                        type="button"
                        onClick={onDone}
                        data-testid="contact-done"
                        className="md:hidden inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#00A3FF]/15 border border-[#00A3FF]/40 text-[#00A3FF] hover:bg-[#00A3FF]/25 text-xs font-bold uppercase tracking-wider transition-colors"
                    >
                        Done
                    </button>
                )}
            </div>
        </div>
    );
}
